import {useEffect, useRef} from 'react';
import fabric from '~/common/canvas/fabric';

const BASE_STEP = 1;
const SHIFT_STEP = 10;

export const Direction = {
  LEFT: 0,
  UP: 1,
  RIGHT: 2,
  DOWN: 3,
};

export const useKeyboardControls = (canvas) => {
  const shiftPressed = useRef(false);
  const intervalsRef = useRef({
    left: null,
    up: null,
    right: null,
    down: null,
  });
  const activeObject = canvas?.getActiveObject();

  const moveSelected = (direction) => {
    const stepLength = shiftPressed.current ? SHIFT_STEP : BASE_STEP;

    if (activeObject) {
      switch (direction) {
        case Direction.LEFT:
          activeObject.left -= stepLength;
          break;
        case Direction.UP:
          activeObject.top -= stepLength;
          break;
        case Direction.RIGHT:
          activeObject.left += stepLength;
          break;
        case Direction.DOWN:
          activeObject.top += stepLength;
          break;
        default:
          break;
      }
      activeObject.setCoords();
      canvas?.renderAll();
    }
  };

  const startInterval = (direction, intervalKey) => {
    intervalsRef.current[intervalKey] = setInterval(() => moveSelected(direction), 50);
  };

  const stopInterval = (intervalKey) => {
    clearInterval(intervalsRef.current[intervalKey]);
    intervalsRef.current[intervalKey] = null;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    const onKeyDown = (options) => {
      if (options.repeat) return;

      const key = options.which || options.keyCode;

      switch (key) {
        case 16: // Shift
          shiftPressed.current = true;
          break;
        case 37: // Left
          startInterval(Direction.LEFT, 'left');
          break;
        case 38: // Up
          startInterval(Direction.UP, 'up');
          break;
        case 39: // Right
          startInterval(Direction.RIGHT, 'right');
          break;
        case 40: // Down
          startInterval(Direction.DOWN, 'down');
          break;
        default:
      }
    };

    const onKeyUp = (options) => {
      if (options.repeat) return;

      const key = options.which || options.keyCode;

      switch (key) {
        case 16: // Shift
          shiftPressed.current = false;
          break;
        case 37: // Left
          stopInterval('left');
          break;
        case 38: // Up
          stopInterval('up');
          break;
        case 39: // Right
          stopInterval('right');
          break;
        case 40: // Down
          stopInterval('down');
          break;
        default:
      }
    };

    fabric.util.addListener(document.body, 'keydown', onKeyDown);
    fabric.util.addListener(document.body, 'keyup', onKeyUp);

    return () => {
      fabric.util.removeListener(document.body, 'keydown', onKeyDown);
      fabric.util.removeListener(document.body, 'keyup', onKeyUp);

      Object.keys(intervalsRef.current).forEach((key) => {
        clearInterval(intervalsRef.current[key]);
      });
    };
  }, [canvas, canvas?.getObjects()?.length]);
};
